import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Building2, ArrowLeft } from 'lucide-react';
import { ThemeToggle } from '@/components/ThemeToggle';
import Actions from '@/pages/Actions';

interface ClientPortalData {
  client: {
    id: string;
    name: string;
    branding: any;
    domain: string | null;
    subdomain: string | null;
    user_id: string;
  };
  role: string;
}

export default function PortalActions() {
  const { clientId } = useParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [clientData, setClientData] = useState<ClientPortalData | null>(null);
  const [businessPortal, setBusinessPortal] = useState<any>(null);

  useEffect(() => {
    if (user && clientId) {
      fetchClientData()
        .then(() => fetchBusinessPortal())
        .finally(() => setLoading(false));
    }
  }, [user, clientId]);

  const fetchClientData = async () => {
    try {
      // First try membership access
      const { data: membershipData, error: membershipError } = await supabase
        .from('client_memberships')
        .select(`
          role,
          clients!inner(
            id,
            name,
            branding,
            domain,
            subdomain,
            user_id
          )
        `)
        .eq('user_id', user?.id)
        .eq('client_id', clientId)
        .maybeSingle();

      if (membershipData && !membershipError) {
        setClientData({
          client: membershipData.clients,
          role: membershipData.role
        });
        return;
      }

      // Fallback to owner access
      const { data: ownerData, error: ownerError } = await supabase
        .from('clients')
        .select('id, name, branding, domain, subdomain, user_id')
        .eq('id', clientId)
        .eq('user_id', user?.id)
        .single();

      if (ownerError) throw ownerError;

      setClientData({
        client: ownerData,
        role: 'owner'
      });
    } catch (error: any) {
      console.error('Error fetching client data:', error);
      toast({
        title: 'Error',
        description: 'You do not have access to this client portal',
        variant: 'destructive',
      });
      navigate('/portal/dashboard');
    }
  };

  const fetchBusinessPortal = async () => {
    try {
      // Get the client owner's business portal for branding
      if (clientData?.client?.user_id) {
        const { data, error } = await supabase
          .from('clients')
          .select('branding, domain, subdomain')
          .eq('user_id', clientData.client.user_id)
          .eq('settings->>is_business_portal', 'true')
          .maybeSingle();

        if (error) {
          console.error('Error fetching business portal:', error);
          return;
        }

        setBusinessPortal(data);
      }
    } catch (error: any) {
      console.error('Error fetching business portal:', error);
    }
  };

  // Apply business portal branding if available, fallback to client branding
  const branding = businessPortal?.branding || clientData?.client.branding || {};
  const primaryColor = branding.primaryColor || 'hsl(var(--primary))';
  const companyName = branding.companyName || clientData?.client.name;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted/50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!clientData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted/50 flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-lg font-medium mb-2">Access Denied</h3>
          <p className="text-muted-foreground mb-4">
            You do not have permission to access this client portal.
          </p>
          <Button onClick={() => navigate('/portal/dashboard')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen bg-gradient-to-br from-background to-muted/50"
      style={{ 
        '--client-primary': primaryColor,
        '--client-primary-rgb': branding.primaryColorRgb || '255, 255, 255'
      } as React.CSSProperties}
    >
      {/* Header with client branding */}
      <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/portal/dashboard')}
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              <div 
                className="w-2 h-8 rounded-full"
                style={{ backgroundColor: primaryColor }}
              />
              <div>
                <h1 className="text-xl font-semibold">{companyName}</h1>
                <p className="text-sm text-muted-foreground">Action Center</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <Badge variant="outline" className="capitalize">
                {clientData.role}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Main Actions component with client filtering */}
      <Actions 
        isClientPortal={true}
        clientId={clientId}
        clientName={companyName}
        branding={branding}
      />
    </div>
  );
}